import React, { useState } from 'react';
import { BloodPressureRecord } from '@/types/record';
import { classifyBP } from '@/utils/bpClassification';
import { calculateMAP, calculatePulsePressure } from '@/utils/calculations';
import { formatTime, getDayPeriodZh } from '@/utils/dateUtils';
import { StampBadge } from '@/components/common/StampBadge';
import { Button } from '@/components/common/Button';

export interface RecordItemProps {
  record: BloodPressureRecord;
  onEdit?: (record: BloodPressureRecord) => void;
  onDelete?: (id: number) => void;
  compact?: boolean;
  className?: string;
}

export const RecordItem: React.FC<RecordItemProps> = ({
  record,
  onEdit,
  onDelete,
  compact = false,
  className = '',
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const categoryMeta = classifyBP(record.systolic, record.diastolic);
  const pulsePressure = calculatePulsePressure(record.systolic, record.diastolic);
  const mapValue = calculateMAP(record.systolic, record.diastolic);
  const timeFormatted = formatTime(record.measured_time || record.measured_at);
  const periodLabel = getDayPeriodZh(timeFormatted);

  const handleDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(record.id);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const armLabel =
    record.arm === 'left' ? '左臂' : record.arm === 'right' ? '右臂' : null;

  return (
    <div
      className={`
        relative bg-white border border-paper-400 rounded-xs p-3 sm:p-4
        transition-all duration-150 hover:shadow-paper-sm hover:border-ink-700
        ${className}
      `}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Left Side: Time, Arm, and BP / Pulse Values */}
        <div className="flex flex-wrap items-center gap-3 sm:gap-5">
          {/* Time badge */}
          <div className="flex sm:flex-col items-center sm:items-start gap-1.5 sm:gap-0 min-w-[72px]">
            <div className="flex items-center gap-1">
              <span className="font-mono font-bold text-sm sm:text-base text-ink-900">
                {timeFormatted}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-serif text-[11px] text-ink-500">
                {periodLabel}
              </span>
              {armLabel && (
                <span className="font-serif text-[10px] bg-paper-200 text-ink-600 px-1 py-0.2 rounded-xs border border-paper-300">
                  {armLabel}
                </span>
              )}
            </div>
          </div>

          {/* Blood Pressure Numbers (Systolic / Diastolic) */}
          <div className="flex items-baseline gap-1.5 pl-2 sm:pl-4 border-l border-paper-300">
            <span className="font-mono font-black text-2xl sm:text-3xl text-ink-900 tracking-tight">
              {record.systolic}
            </span>
            <span className="font-serif text-lg text-ink-400 font-light">/</span>
            <span className="font-mono font-bold text-xl sm:text-2xl text-ink-800 tracking-tight">
              {record.diastolic}
            </span>
            <span className="font-mono text-xs text-ink-500 ml-0.5">mmHg</span>
          </div>

          {/* Heart Rate / Pulse */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-paper-100 rounded-xs border border-paper-300">
            <span className="text-red-700 text-xs animate-pulse">♥</span>
            <span className="font-mono font-bold text-sm text-ink-900">
              {record.pulse}
            </span>
            <span className="font-serif text-[11px] text-ink-500">bpm</span>
          </div>
        </div>

        {/* Right Side: AHA Stamp Badge & Clinical Indicators & Actions */}
        <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
          {/* MAP & Pulse Pressure (hidden on extra small screens if compact) */}
          {!compact && (
            <div className="hidden md:flex flex-col text-right font-mono text-[11px] text-ink-500">
              <span>脈壓差: {pulsePressure} mmHg</span>
              <span>MAP: {mapValue} mmHg</span>
            </div>
          )}

          {/* AHA Stamp Badge */}
          <StampBadge
            category={categoryMeta.category}
            size="md"
            className="shadow-xs"
          />

          {/* Actions: Edit & Delete */}
          <div className="flex items-center gap-1">
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(record)}
                className="p-1.5 text-ink-600 hover:text-ink-900 hover:bg-paper-200 rounded-xs transition-colors"
                title="編輯紀錄"
                aria-label="編輯紀錄"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}

            {onDelete && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-xs transition-colors"
                title="刪除紀錄"
                aria-label="刪除紀錄"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notes Row (if present) */}
      {record.notes && (
        <div className="mt-2.5 pt-2 border-t border-paper-200 flex items-start gap-2">
          <span className="font-serif text-[11px] text-ink-400 shrink-0 select-none">
            [備註]
          </span>
          <p className="font-serif text-xs text-ink-700 leading-relaxed break-all">
            {record.notes}
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal / Inline Overlay */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 z-10 bg-paper-100/95 backdrop-blur-xs flex items-center justify-between p-3 rounded-xs border-2 border-red-700 animate-in fade-in duration-100">
          <div className="space-y-0.5">
            <p className="font-serif font-bold text-xs text-red-900">
              確認刪除此筆 {timeFormatted} ({record.systolic}/{record.diastolic} mmHg) 紀錄？
            </p>
            <p className="font-serif text-[11px] text-ink-600">
              刪除後將無法復原。
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              取消
            </Button>
            <Button
              type="button"
              variant="danger"
              size="sm"
              isLoading={isDeleting}
              onClick={handleDelete}
            >
              確定刪除
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
